import { queryOptions } from '@tanstack/react-query';
import { apiClient } from '@/api/axios';
import { API } from '@/api/endpoints';
import { downloadApiFile } from '@/utils/download';
import {
  pcmpUrgentBatchReportSchema,
  pcmpUrgentPublishResultSchema,
  pcmpUrgentRollbackResultSchema,
  pcmpUrgentStageResultSchema,
  type PcmpUrgentBatchReport,
  type PcmpUrgentPublishResult,
  type PcmpUrgentRollbackPayload,
  type PcmpUrgentRollbackResult,
  type PcmpUrgentStagePayload,
  type PcmpUrgentStageResult,
} from '../types/pcmpUrgent.types';
import { pcmpRequestQueryKeys } from './pcmpRequests.api';

export async function stagePcmpUrgentBatch(
  payload: PcmpUrgentStagePayload,
): Promise<PcmpUrgentStageResult> {
  const res = await apiClient.post(API.pcmpUrgent.stage, payload);
  return pcmpUrgentStageResultSchema.parse(res.data);
}

export async function publishPcmpUrgentBatch(
  batchNo: number,
  comment: string | undefined,
): Promise<PcmpUrgentPublishResult> {
  const res = await apiClient.post(API.pcmpUrgent.publish(batchNo), { comment });
  return pcmpUrgentPublishResultSchema.parse(res.data);
}

export async function rollbackPcmpUrgentBatch(
  batchNo: number,
  payload: PcmpUrgentRollbackPayload,
): Promise<PcmpUrgentRollbackResult> {
  const res = await apiClient.post(API.pcmpUrgent.rollback(batchNo), payload);
  return pcmpUrgentRollbackResultSchema.parse(res.data);
}

export const pcmpUrgentBatchReportQueryOptions = (batchNo: number) =>
  queryOptions({
    queryKey: pcmpRequestQueryKeys.report(batchNo),
    queryFn: async (): Promise<PcmpUrgentBatchReport> => {
      const res = await apiClient.get(API.pcmpUrgent.report(batchNo));
      return pcmpUrgentBatchReportSchema.parse(res.data);
    },
    staleTime: 0,
  });

/** The API names the file the same way; its header is not readable cross-origin. */
export function pcmpUrgentBatchXlsxFilename(batchNo: number, date: string): string {
  return `CMP_Urgent_Batch_${batchNo}_${date}.xlsx`;
}

export function downloadPcmpUrgentBatchXlsx(batchNo: number, date: string): Promise<void> {
  return downloadApiFile(
    API.pcmpUrgent.exportXlsx(batchNo),
    pcmpUrgentBatchXlsxFilename(batchNo, date),
  );
}
